import { NgOptimizedImage } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  effect,
  inject,
  untracked,
} from "@angular/core";
import { BELocalizationService } from "@core/services/global/localization/be-localization.service";
import { Language } from "@core/services/global/localization/interfaces/localization.interfaces";
import { UserStorageService } from "@core/services/utils/user-storage.service";
import { DistributionService } from "@services/business/distribution-services/distribution.service";

@Component({
  selector: "lq-languages-selector",
  templateUrl: "./languages.component.html",
  styleUrl: "./languages.component.scss",
  imports: [NgOptimizedImage],
})
export class LanguagesComponent implements OnInit {
  private readonly localizationService = inject(BELocalizationService);
  private readonly distributionService = inject(DistributionService);
  private readonly userStorageService = inject(UserStorageService);

  @Output() public clickOnShow = new EventEmitter<unknown>();
  @ViewChild("listContainer") protected listContainerRef: ElementRef;
  @ViewChild("selectedDiv") protected selectedDiv: ElementRef;

  protected isHover = false;
  protected isHoverChild = false;
  protected showAll = false;
  protected dropdownTop = 0;
  protected dropdownRight = 0;
  protected changeSemaphore = true;
  protected items: Language[] = [];
  protected selectedItem: Language;
  protected participantId?: string;

  onFocus(): void {
    this.isHover = true;
    this.showAll = true;
    this.changeLanguage(this.selectedItem);
  }

  onMouseLeave(): void {
    this.isHover = false;
    this.showAll = this.isHover || this.isHoverChild;
  }

  onBlur(): void {
    this.isHover = false;
    this.showAll = this.isHover || this.isHoverChild;
  }

  onDropdownMouseOver(): void {
    this.isHoverChild = true;
  }

  onDropdownFocus(): void {
    this.isHoverChild = true;
  }

  onDropdownMouseLeave(): void {
    this.isHoverChild = false;
    this.showAll = this.isHover || this.isHoverChild;
  }

  onDropdownBlur(): void {
    this.isHoverChild = false;
    this.showAll = this.isHover || this.isHoverChild;
  }

  changeLanguage(item: Language) {
    if (item.selected) {
      if (this.showAll) {
        const selectedItemRect =
          this.selectedDiv.nativeElement.getBoundingClientRect();
        this.dropdownTop = selectedItemRect.top + selectedItemRect.height;
        this.dropdownRight = window.innerWidth - selectedItemRect.right;
      }
    } else {
      if (this.changeSemaphore) {
        this.changeSemaphore = false;
        for (const item of this.items) {
          item.selected = false;
        }
        item.selected = true;
        this.showAll = false;

        if (this.participantId) {
          this.distributionService
            .setParticipantDefaultLanguage(this.participantId, item.id)
            .subscribe();
        }

        this.localizationService.switchLanguage(
          item.id,
          () => {
            window.location.reload();
          },
          () => {
            this.changeSemaphore = true;
          }
        );
      }
    }
  }

  private reorderItems(): void {
    this.items = this.items.sort((a: Language, b: Language) => {
      if (a.selected && !b.selected) {
        return -1; // a is selected and b is not, so a should come first
      } else if (!a.selected && b.selected) {
        return 1; // b is selected and a is not, so b should come first
      } else {
        return (
          a.description
            .toUpperCase()
            .localeCompare(b.description.toUpperCase()) ||
          a.id.localeCompare(b.id)
        );
      }
    });
  }

  unselectedItems(): Language[] {
    return this.items.filter((item: Language) => !item.selected);
  }

  constructor() {
    let isFirstRun = true;

    effect(() => {
      const registeredUser = this.userStorageService.registeredUserSignal();

      if (isFirstRun) {
        isFirstRun = false;
        return;
      }

      if (!registeredUser) {
        return;
      }

      untracked(() => this.refreshUserLanguage());
    });
  }

  ngOnInit(): void {
    this.participantId = this.userStorageService.participantId ?? undefined;
    this.loadLanguages();
  }

  private loadLanguages(): void {
    const languages = this.localizationService.getLanguages();
    if (languages && languages.length > 0) {
      this.checkUserDefaultLanguage(languages);
    } else {
      this.items = [];
    }
  }

  private checkUserDefaultLanguage(languages: Language[]): void {
    // If user is logged in and has a participant ID, check their default language
    if (this.participantId) {
      // Get user data directly without triggering the observable to avoid recursion
      const user = this.userStorageService.registeredUser;
      if (user) {
        const userDefaultLang = user.defaultLanguage;
        const sessionLang = this.localizationService.getSessionLanguage();

        // If user has a default language and it's different from session, update it
        if (userDefaultLang && userDefaultLang !== sessionLang) {
          this.localizationService.switchLanguage(
            userDefaultLang,
            () => {
              // Reload the page to apply the new language translations
              window.location.reload();
            },
            (err: unknown) => {
              console.warn("Failed to switch to user default language:", err);
              this.setupLanguageItems(languages, sessionLang);
            }
          );
        } else {
          this.setupLanguageItems(languages, userDefaultLang || sessionLang);
        }
      } else {
        // User data not available yet, use session language
        const sessionLang = this.localizationService.getSessionLanguage();
        this.setupLanguageItems(languages, sessionLang);
      }
    } else {
      const sessionLang = this.localizationService.getSessionLanguage();
      this.setupLanguageItems(languages, sessionLang);
    }
  }

  private setupLanguageItems(languages: Language[], currentLang: string): void {
    const newItems =
      languages.map((item: Language) => {
        let image = "";
        const selected = item.id === currentLang;
        if (selected) {
          image = "/assets/images/png/icons/selected.png";
        } else {
          image = "/assets/images/png/icons/unselected.png";
        }
        const newItem: Language = {
          ...item,
          icon: image,
          selected: selected,
        };
        if (newItem.selected) {
          this.selectedItem = newItem;
        }
        return newItem;
      }) || [];

    this.items = [...newItems];
    this.reorderItems();
  }

  protected get existItems() {
    return this.items.length > 0;
  }

  /**
   * Public method to refresh language after user login
   * Can be called from the callback component or other components after login
   */
  public refreshUserLanguage(): void {
    this.loadLanguages();

    const participantId = this.userStorageService.participantId;
    if (!participantId) {
      return;
    }
    this.participantId = participantId;
  }
}
